import { isSameDay, isValid, parse } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import React, { useEffect, useMemo } from "react";
import { useDebounceValue } from "usehooks-ts";

import { faCircleInfo } from "@performyard/fontawesome/pro-regular-svg-icons";
import {
	ButtonStandard,
	DatePicker,
	Icon,
	InputText,
	InputWrapper,
	ModalBase,
	SingleSelectInput,
	Text,
	Tooltip,
} from "@performyard/py-components";
import type { VideoConferencingProviderEnum } from "@performyard/service-contracts";

import InputTextArea from "../../../../../components/Inputs/InputTextArea";
import { Checkboxes } from "../../../../../components/Inputs/SelectionOptions/Checkboxes";
import { VIDEO_CONFERENCING_PROVIDER_CONFIG } from "../../../../../components/VideoConferencing";

import { useLatestMeetingCarryOverItems } from "../../../../../api/hooks";
import { useCalendarIntegrationStatus, useVideoConferencingEligibility } from "../../hooks";

import { hasMeetingStarted } from "../../../utils/meetingTiming";

import { config } from "../../config";
import type { FormErrors, MeetingFormSchema, RepeatsPreset } from "./meeting-form-schema";
import {
	type MeetingFormValuesWithOptionalDates,
	SERIES_ENDS_OPTIONS,
	generateTimeOptions,
	getRepeatsOptions,
	handleEndDateChange,
	handleStartDateChange,
	handleStartTimeChange,
	isWeekday,
} from "./meeting-form-utils";

export type MeetingFormData = {
	name: string;
	description: string;
	startDateTime: Date;
	endDateTime: Date;
	carryOver: boolean;
	addToCalendar: boolean;
	videoConferencingProvider: VideoConferencingProviderEnum | null;
};

/**
 * Recurrence choices, passed alongside the meeting fields rather than mixed into them
 * so the ad-hoc meeting consumers can't forward series-only keys to their endpoints.
 */
export type MeetingRecurrenceFormData = {
	repeats: RepeatsPreset;
	seriesEndDate?: Date;
};

type MeetingFormProps = {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (data: MeetingFormData, recurrence: MeetingRecurrenceFormData) => void;
	formValues: MeetingFormValuesWithOptionalDates;
	onFormChange: (newValues: Partial<MeetingFormSchema>) => void;
	validationErrors: FormErrors;
	submitLabel: string;
	isPending: boolean;
	title: string;
	subtitle?: string;
	showCarryOver?: boolean;
	/** Renders the series-level carryover copy instead of the previous-meeting item count. */
	carryOverVariant?: "count" | "series";
	/** Shows the calendar + video conferencing options. Each one self-gates on its own connection
	 *  eligibility, so passing this still hides an integration the user hasn't connected. */
	showMeetingIntegrations?: boolean;
	/** Shows the series-scoped "Add Series to My Calendar" checkbox (recurring meeting modals
	 *  only) — deliberately separate from showMeetingIntegrations so the per-meeting calendar
	 *  and video conferencing controls stay hidden on the series forms. */
	showSeriesCalendarToggle?: boolean;
	/** Shows the video conferencing controls on the series forms, where the provider choice
	 *  means "create every meeting in this series with a link" — separate from
	 *  showMeetingIntegrations so the per-meeting calendar toggle stays hidden there. */
	showSeriesVideoConferencing?: boolean;
	/** Provider already stored on an edited series. Kept separate from live connections so a
	 * disconnected provider remains visible and can be explicitly cleared. */
	persistedVideoConferencingProvider?: VideoConferencingProviderEnum | null;
	/** The zone the form's date and time belong to, when that isn't the browser's — a series carries
	 *  its own. Only the "has it started?" gate reads it: parsing a series' wall clock locally reads
	 *  an occurrence as started up to the offset difference early for an editor east of the series. */
	formTimezone?: string;
	/** Shows the "Repeats" / "End Repeat" recurrence controls (recurring meeting modals only). */
	showRecurrence?: boolean;
	/** Labels the selected preset with the series' own cadence instead of one derived from the picked date. */
	selectedRepeatsLabel?: string;
	startDateLabel?: string;
	calendarUpdateNotification?: string;
	workspaceOptions?: { label: string; value: string }[];
};

const MeetingForm = ({
	isOpen,
	onClose,
	onSubmit,
	formValues,
	onFormChange,
	validationErrors,
	submitLabel,
	isPending,
	title,
	subtitle,
	showCarryOver = false,
	carryOverVariant = "count",
	showMeetingIntegrations = true,
	showSeriesCalendarToggle = false,
	showSeriesVideoConferencing = false,
	persistedVideoConferencingProvider,
	formTimezone,
	showRecurrence = false,
	selectedRepeatsLabel,
	startDateLabel = "Start Date",
	calendarUpdateNotification,
	workspaceOptions,
}: MeetingFormProps): React.ReactElement => {
	const { isCalendarIntegrationEnabled, isCalendarStatusLoading } = useCalendarIntegrationStatus();
	const { connectedProviders, isLoading: isEligibilityLoading } = useVideoConferencingEligibility();

	const startDateTime = useMemo(() => {
		if (!formValues.startDate || !isValid(formValues.startDate)) {
			return new Date();
		}
		return parse(formValues.startTime, "HH:mm", formValues.startDate);
	}, [formValues.startTime, formValues.startDate]);

	// The instant the form's wall clock actually names. Left out of `startDateTime` itself, which
	// feeds the carry-over lookup in the browser's zone.
	const startTimePassed = hasMeetingStarted(
		formTimezone ? zonedTimeToUtc(startDateTime, formTimezone) : startDateTime
	);
	const startDateTimeLabel = startDateLabel === "Start Date" ? "Start" : startDateLabel;

	const persistedProviderIsDisconnected =
		!!persistedVideoConferencingProvider &&
		!connectedProviders.some(({ provider }) => provider === persistedVideoConferencingProvider);
	const disconnectedProviderOption =
		persistedProviderIsDisconnected && persistedVideoConferencingProvider
			? {
					value: persistedVideoConferencingProvider,
					label: `${VIDEO_CONFERENCING_PROVIDER_CONFIG[persistedVideoConferencingProvider].shortName} (Disconnected)`,
				}
			: undefined;
	const conferencingProviderOptions = [
		...connectedProviders.map(({ provider }) => ({
			value: provider,
			label: VIDEO_CONFERENCING_PROVIDER_CONFIG[provider].shortName,
		})),
		...(disconnectedProviderOption ? [disconnectedProviderOption] : []),
	];

	// A single live provider renders a checkbox. A disconnected stored provider always
	// renders the clearable select, including when it is the only option.
	const singleConnectedProvider =
		connectedProviders.length === 1 && !persistedProviderIsDisconnected ? connectedProviders[0].provider : null;

	// The per-meeting and series forms share the conferencing controls — on a series
	// form the same provider choice means "every meeting in this series".
	const showVideoConferencingControls = (showMeetingIntegrations || showSeriesVideoConferencing) && !startTimePassed;
	const addToCalendarOption = {
		value: "addToCalendar",
		label: showSeriesCalendarToggle ? "Add Series to My Calendar" : "Add Meeting to My Calendar",
	};

	const [debouncedStartDate] = useDebounceValue(startDateTime, 100);

	const isCountBasedCarryOver = showCarryOver && carryOverVariant === "count";

	const { data: latestMeetingCarryOverItems, isLoading: isLatestMeetingCarryOverItemsLoading } =
		useLatestMeetingCarryOverItems(
			formValues.workspaceId ?? "",
			debouncedStartDate,
			isCountBasedCarryOver && !!formValues.workspaceId
		);

	const carryOverItemsCount = latestMeetingCarryOverItems?.count ?? 0;

	useEffect(() => {
		if (isCountBasedCarryOver && carryOverItemsCount === 0 && formValues.carryOver) {
			onFormChange({ carryOver: false });
		}
	}, [isCountBasedCarryOver, carryOverItemsCount, formValues.carryOver, onFormChange]);

	// "Every Weekday (M-F)" isn't offered for newly-picked weekend dates. An existing
	// weekday series can have its current meeting moved to a weekend, though, and an
	// unrelated series edit must preserve that stored cadence.
	useEffect(() => {
		if (
			showRecurrence &&
			!selectedRepeatsLabel &&
			formValues.repeats === "weekday" &&
			formValues.startDate &&
			isValid(formValues.startDate) &&
			!isWeekday(formValues.startDate)
		) {
			onFormChange({ repeats: "weekly" });
		}
	}, [showRecurrence, selectedRepeatsLabel, formValues.repeats, formValues.startDate, onFormChange]);

	// If the calendar integration becomes unusable (e.g. an expired connection cleared after a
	// failed submit), uncheck "add to calendar" so the user can retry and succeed without it.
	// Never while eligibility is still LOADING — unchecking a prefilled series toggle during
	// that window would read back as an explicit detach once the edit modal diffs the form.
	useEffect(() => {
		if (!isCalendarStatusLoading && !isCalendarIntegrationEnabled && formValues.addToCalendar) {
			onFormChange({ addToCalendar: false });
		}
	}, [isCalendarStatusLoading, isCalendarIntegrationEnabled, formValues.addToCalendar, onFormChange]);

	// A start time can be edited into the past after a provider was picked; drop the now-hidden selection.
	useEffect(() => {
		if (startTimePassed && formValues.videoConferencingProvider) {
			onFormChange({ videoConferencingProvider: null });
		}
	}, [startTimePassed, formValues.videoConferencingProvider, onFormChange]);

	// A provider can disconnect while the form is open (e.g. in another tab); discard a new
	// selection that's no longer offered so a stale submit doesn't 400. An edited series returns
	// to its persisted provider rather than turning that disconnect into a series-wide removal.
	useEffect(() => {
		if (
			!isEligibilityLoading &&
			formValues.videoConferencingProvider &&
			formValues.videoConferencingProvider !== persistedVideoConferencingProvider &&
			!connectedProviders.some(({ provider }) => provider === formValues.videoConferencingProvider)
		) {
			onFormChange({ videoConferencingProvider: persistedVideoConferencingProvider ?? null });
		}
	}, [
		isEligibilityLoading,
		connectedProviders,
		formValues.videoConferencingProvider,
		persistedVideoConferencingProvider,
		onFormChange,
	]);

	const carryOverItemsLabel = `Carryover ${carryOverItemsCount} unchecked item${carryOverItemsCount === 1 ? "" : "s"} from the previous meeting`;
	const carryOverItemsInputDisabled = isPending || isLatestMeetingCarryOverItemsLoading || carryOverItemsCount === 0;
	const seriesCarryOverLabel = "Carry over incomplete items in this series";
	// Every preset is labelled from the picked date, which is what selecting it would
	// build. The one already selected is the exception: it stands for the stored rule,
	// which a single-meeting edit can leave sitting on a day that rule never selects.
	const dateDerivedRepeatsOptions = getRepeatsOptions(formValues.startDate);
	const repeatsOptions = dateDerivedRepeatsOptions.map((option) =>
		selectedRepeatsLabel && option.value === formValues.repeats
			? { ...option, label: selectedRepeatsLabel }
			: option
	);
	if (selectedRepeatsLabel && !dateDerivedRepeatsOptions.some((option) => option.value === formValues.repeats)) {
		repeatsOptions.unshift({ value: formValues.repeats, label: selectedRepeatsLabel });
	}

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
		e.preventDefault();

		const recurrenceData: MeetingRecurrenceFormData = {
			repeats: formValues.repeats,
			seriesEndDate: showRecurrence && formValues.seriesEnds === "onDate" ? formValues.seriesEndDate : undefined,
		};

		// If dates are not set, validation will fail in the parent component
		if (!formValues.startDate || !formValues.endDate) {
			onSubmit(
				{
					name: formValues.name.trim(),
					description: formValues.description?.trim() || "",
					startDateTime: new Date(),
					endDateTime: new Date(),
					carryOver: formValues.carryOver,
					addToCalendar: formValues.addToCalendar,
					videoConferencingProvider: formValues.videoConferencingProvider,
				},
				recurrenceData
			);
			return;
		}

		const startDT = parse(formValues.startTime, "HH:mm", formValues.startDate);
		const endDT = parse(formValues.endTime, "HH:mm", formValues.endDate);

		const data = {
			name: formValues.name.trim(),
			description: formValues.description?.trim() || "",
			startDateTime: startDT,
			endDateTime: endDT,
			carryOver: formValues.carryOver,
			addToCalendar: formValues.addToCalendar,
			videoConferencingProvider: formValues.videoConferencingProvider,
		};

		onSubmit(data, recurrenceData);
	};

	const timeOptions = useMemo(() => generateTimeOptions(formValues.startDate ?? new Date()), [formValues.startDate]);

	const availableStartTimeOptions = timeOptions;

	const availableEndTimeOptions = useMemo(() => {
		const endTimeOptions = generateTimeOptions(formValues.endDate ?? new Date());

		// Only filter time options if both dates are valid and on the same day
		if (
			formValues.startDate &&
			formValues.endDate &&
			isValid(formValues.startDate) &&
			isValid(formValues.endDate) &&
			isSameDay(formValues.startDate, formValues.endDate)
		) {
			return endTimeOptions.filter((option) => option.value > formValues.startTime);
		}
		return endTimeOptions;
	}, [formValues.startDate, formValues.endDate, formValues.startTime]);

	return (
		<ModalBase isOpen={isOpen} onClose={onClose} dangerouslySetClassName="w-[600px]">
			<div className="px-6 pb-4 text-center">
				<Text size="sub-heading" weight="semi-bold">
					{title}
				</Text>
				{(subtitle ?? calendarUpdateNotification) && (
					<div className="mt-4">
						<Text size="paragraph-3" weight="regular">
							{subtitle ?? calendarUpdateNotification}
						</Text>
					</div>
				)}
			</div>

			<form id="meeting-form" data-testid="meeting-form" onSubmit={handleSubmit} className="px-6 pb-6">
				{workspaceOptions && (
					<>
						<InputWrapper
							id="workspace-select"
							label="Meeting Workspace"
							error={validationErrors.workspaceId}
						>
							<SingleSelectInput<string>
								selectionVariant="combobox"
								options={workspaceOptions}
								fetchOptions={async (searchTerm) =>
									workspaceOptions.filter((o) =>
										o.label.toLowerCase().includes(searchTerm.toLowerCase())
									)
								}
								value={formValues.workspaceId}
								onChange={(value) => onFormChange({ workspaceId: value ?? undefined })}
								placeholder="Make a selection"
								disabled={isPending}
								data-testid="workspace-select"
							/>
						</InputWrapper>
						<div className="h-4" />
					</>
				)}
				<InputWrapper
					id="meeting-name-input"
					label="Meeting Name"
					error={
						validationErrors.name
							? validationErrors.name
							: formValues.name.length > 280
								? "Name cannot exceed 280 characters"
								: undefined
					}
				>
					<InputText
						placeholder="Meeting name here"
						value={formValues.name}
						onChange={(val) => onFormChange({ name: val })}
						disabled={isPending}
						clearable={true}
						maxLength={280}
						data-testid="meeting-name-input"
					/>
				</InputWrapper>
				<div className="h-4" />
				<div className="grid min-w-0 grid-cols-2 gap-4" data-testid="meeting-date-time-layout">
					<InputWrapper
						id="start-date-time-input"
						label={startDateTimeLabel}
						group
						disableLabelClick
						dangerouslySetClassName="min-w-0 [&>div]:min-w-0"
					>
						<div className="grid min-w-0 grid-cols-[160px_1fr] gap-2" data-testid="start-date-time-fields">
							<InputWrapper
								id="start-date-input"
								error={validationErrors.startDate}
								dangerouslySetClassName="min-w-0 [&>div]:min-w-0"
							>
								<label className="sr-only" htmlFor="start-date-input">
									{startDateLabel}
								</label>
								<DatePicker
									id="start-date-input"
									dangerouslySetClassName="min-w-0 [&_input]:min-w-0"
									allowEmpty
									value={formValues.startDate ?? null}
									onChange={(val) => handleStartDateChange(val, formValues, onFormChange)}
									disabled={isPending}
								/>
							</InputWrapper>
							<InputWrapper
								id="start-time-input"
								error={validationErrors.startTime}
								dangerouslySetClassName="min-w-0 [&>div]:min-w-0"
							>
								<label className="sr-only" htmlFor="start-time-input">
									Start Time
								</label>
								<SingleSelectInput<string>
									id="start-time-input"
									options={availableStartTimeOptions}
									value={formValues.startTime}
									onChange={(option) =>
										handleStartTimeChange({ value: option ?? "" }, formValues, onFormChange)
									}
									placeholder="Select a start time"
									ariaLabel="Start Time"
									dangerouslySetClassName="min-w-0 [&_input]:min-w-0"
									disabled={isPending}
									data-testid="start-time-select"
								/>
							</InputWrapper>
						</div>
					</InputWrapper>
					<InputWrapper
						id="end-date-time-input"
						label="End"
						group
						disableLabelClick
						dangerouslySetClassName="min-w-0 [&>div]:min-w-0"
					>
						<div className="grid min-w-0 grid-cols-[160px_1fr] gap-2" data-testid="end-date-time-fields">
							<InputWrapper
								id="end-date-input"
								error={validationErrors.endDate}
								dangerouslySetClassName="min-w-0 [&>div]:min-w-0"
							>
								<label className="sr-only" htmlFor="end-date-input">
									End Date
								</label>
								<DatePicker
									id="end-date-input"
									dangerouslySetClassName="min-w-0 [&_input]:min-w-0"
									allowEmpty
									value={formValues.endDate ?? null}
									onChange={(val) => handleEndDateChange(val, formValues, onFormChange)}
									disabled={isPending}
								/>
							</InputWrapper>
							<InputWrapper
								id="end-time-input"
								error={validationErrors.endTime}
								dangerouslySetClassName="min-w-0 [&>div]:min-w-0"
							>
								<label className="sr-only" htmlFor="end-time-input">
									End Time
								</label>
								<SingleSelectInput<string>
									id="end-time-input"
									options={availableEndTimeOptions}
									value={formValues.endTime}
									onChange={(option) => {
										onFormChange({ endTime: option ?? "" });
									}}
									placeholder="Select an end time"
									ariaLabel="End Time"
									dangerouslySetClassName="min-w-0 [&_input]:min-w-0"
									disabled={isPending}
									data-testid="end-time-select"
								/>
							</InputWrapper>
						</div>
					</InputWrapper>
				</div>
				{showRecurrence && (
					<>
						<div className="h-4" />
						<div className="grid min-w-0 grid-cols-2 gap-4" data-testid="meeting-recurrence-layout">
							<InputWrapper
								id="repeats-input"
								label="Repeats"
								dangerouslySetClassName="min-w-0 [&>div]:min-w-0"
								data-testid="repeats-field"
							>
								<SingleSelectInput<string>
									options={repeatsOptions}
									value={formValues.repeats}
									onChange={(option) =>
										onFormChange({ repeats: (option ?? "weekly") as RepeatsPreset })
									}
									disabled={isPending}
									data-testid="repeats-select"
									dangerouslySetClassName="min-w-0 [&_input]:min-w-0"
								/>
							</InputWrapper>
							<div
								className="grid min-w-0 grid-cols-[1fr_160px] gap-2"
								data-testid="meeting-recurrence-end-fields"
							>
								<InputWrapper
									id="series-ends-input"
									label="End Repeat"
									dangerouslySetClassName="min-w-0 [&>div]:min-w-0"
									data-testid="series-ends-field"
								>
									<SingleSelectInput<string>
										options={[...SERIES_ENDS_OPTIONS]}
										value={formValues.seriesEnds}
										onChange={(option) =>
											onFormChange({
												seriesEnds: (option ?? "never") as MeetingFormSchema["seriesEnds"],
											})
										}
										disabled={isPending}
										data-testid="series-ends-select"
										dangerouslySetClassName="min-w-0 [&_input]:min-w-0"
									/>
								</InputWrapper>
								{formValues.seriesEnds === "onDate" && (
									<InputWrapper
										id="series-end-date-input"
										label="On"
										error={validationErrors.seriesEndDate}
										dangerouslySetClassName="min-w-0 [&>div]:min-w-0"
										data-testid="series-end-date-field"
									>
										<DatePicker
											allowEmpty
											value={formValues.seriesEndDate ?? null}
											onChange={(val) => onFormChange({ seriesEndDate: val ?? undefined })}
											disabled={isPending}
											dangerouslySetClassName="min-w-0 [&_input]:min-w-0"
										/>
									</InputWrapper>
								)}
							</div>
						</div>
					</>
				)}
				<div className="h-4" />
				<InputTextArea
					value={formValues.description || ""}
					onChange={(val) => onFormChange({ description: val })}
					placeholder="Your meeting description and link"
					minRows={6}
					maxRows={6}
					disabled={isPending}
					label="Description"
					labelSize="paragraph-3"
					errorMessage={
						validationErrors.description
							? validationErrors.description
							: (formValues.description?.length || 0) > config.meetingDescriptionMaxLength
								? `Descriptions cannot exceed ${config.meetingDescriptionMaxLength} characters`
								: ""
					}
					data-testid="meeting-description-input"
				/>
				{showCarryOver && carryOverVariant === "count" && (
					<>
						<div className="h-4" />
						<Checkboxes
							options={[{ value: "carryOver", label: carryOverItemsLabel }]}
							value={formValues.carryOver ? [{ value: "carryOver", label: carryOverItemsLabel }] : []}
							disabled={carryOverItemsInputDisabled ? ["carryOver"] : []}
							onChange={() => onFormChange({ carryOver: !formValues.carryOver })}
						/>
					</>
				)}
				{showCarryOver && carryOverVariant === "series" && (
					<>
						<div className="h-4" />
						<div className="flex items-center">
							{/* Checkboxes renders through InputWrapper, which is w-full — without
							    constraining it the info icon is pushed to the far edge of the row. */}
							<div className="w-fit">
								<Checkboxes
									options={[{ value: "carryOver", label: seriesCarryOverLabel }]}
									value={
										formValues.carryOver
											? [{ value: "carryOver", label: seriesCarryOverLabel }]
											: []
									}
									disabled={isPending ? ["carryOver"] : []}
									onChange={() => onFormChange({ carryOver: !formValues.carryOver })}
								/>
							</div>
							<Tooltip
								content="Incomplete items automatically move to the next meeting for all meetings in the series."
								placement="top"
							>
								<Icon icon={faCircleInfo} size={12} color="neutral-7" />
							</Tooltip>
						</div>
					</>
				)}
				{(showSeriesCalendarToggle || showMeetingIntegrations) && isCalendarIntegrationEnabled && (
					<>
						<div className="h-4" />
						<Checkboxes
							options={[addToCalendarOption]}
							value={formValues.addToCalendar ? [addToCalendarOption] : []}
							disabled={isPending ? ["addToCalendar"] : []}
							onChange={() => onFormChange({ addToCalendar: !formValues.addToCalendar })}
						/>
					</>
				)}
				{showVideoConferencingControls && singleConnectedProvider && (
					<>
						<div className="h-4" />
						<Checkboxes
							options={[{ value: singleConnectedProvider, label: "Add Video Conferencing" }]}
							value={
								formValues.videoConferencingProvider === singleConnectedProvider
									? [{ value: singleConnectedProvider, label: "Add Video Conferencing" }]
									: []
							}
							disabled={isPending ? [singleConnectedProvider] : []}
							onChange={() =>
								onFormChange({
									videoConferencingProvider:
										formValues.videoConferencingProvider === singleConnectedProvider
											? null
											: singleConnectedProvider,
								})
							}
						/>
					</>
				)}
				{showVideoConferencingControls &&
					(connectedProviders.length > 1 || persistedProviderIsDisconnected) && (
						<>
							<div className="h-4" />
							<InputWrapper id="video-conferencing-select" label="Video Conferencing">
								<SingleSelectInput<VideoConferencingProviderEnum>
									options={conferencingProviderOptions}
									value={formValues.videoConferencingProvider ?? undefined}
									onChange={(value) => onFormChange({ videoConferencingProvider: value ?? null })}
									placeholder="Make a selection"
									isClearable
									disabled={isPending}
									data-testid="video-conferencing-select"
								/>
							</InputWrapper>
						</>
					)}
			</form>

			<div className="flex justify-center gap-2 px-6 pb-6">
				<div key="cancelButton" className="w-fit min-w-[90px]">
					<ButtonStandard
						variant="outline"
						color="neutral"
						size="large"
						onClick={onClose}
						disabled={isPending}
					>
						Cancel
					</ButtonStandard>
				</div>
				<div key="submitButton" className="w-fit min-w-[90px]">
					<ButtonStandard
						size="large"
						color="primary"
						type="submit"
						form="meeting-form"
						disabled={isPending || isLatestMeetingCarryOverItemsLoading}
						data-testid="meeting-form-submit-button"
					>
						{submitLabel}
					</ButtonStandard>
				</div>
			</div>
		</ModalBase>
	);
};

export default MeetingForm;
